import { Button, Icon, Tooltip, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Notification } from '@hero/atoms';
import { GiftCollectionSizeModal } from '@hero/components/modals';

import { DetailItem } from '../../DetailItem';
import { RearrangeModal } from '../RearrangeModal';

import * as S from './style';

function VerticalListHeader({
  isDetailItemEditable,
  products,
  updateProducts,
  hasAlert = true,
  setShowProductModal,
  isFetching,
  noItemsFound,
  isLiveFeature,
  rearrangeEffectiveType,
  originalProductLength
}) {
  const { t } = useTranslation();
  const [rearrangeModalOpen, setRearrangeModalOpen] = useState(false);
  const [isRearranged, setIsRearranged] = useState(false);

  const collectionSize = products?.length || 0;

  const handleRearrangeModalClose = () => {
    setRearrangeModalOpen(false);
  };

  const GIFT_ALERT_LIMIT = 12;
  const hasGiftAlert = hasAlert && collectionSize >= GIFT_ALERT_LIMIT;

  const [showSizeWarnOpen, setShowSizeWarnOpen] = useState(false);
  const openProductModal = () => {
    setShowProductModal(true);
  };

  const openSizeWarnModal = () => {
    setShowSizeWarnOpen(true);
  };

  const sizeModalHandler = () => {
    setShowSizeWarnOpen(false);
    openProductModal();
  };

  return (
    <S.VerticalListHeader>
      {!isFetching && hasGiftAlert && (
        <Notification
          status="info"
          title={t('giftCollectionSize')}
          message={
            <Typography
              style={{
                display: 'inline-flex',
                gap: 4,
                alignItems: 'center',
                flexWrap: 'wrap',
                width: '100%'
              }}
              variant="body2"
              emphasis="weak"
              as="div"
            >
              <Trans
                i18nKey="giftCollectionSizeDescription"
                components={{
                  b: <b />,
                  a: (
                    <Button
                      onClick={openSizeWarnModal}
                      variant="link"
                      color="neutral"
                      emphasis="800"
                    />
                  )
                }}
                values={{
                  itemCount: collectionSize
                }}
              />
            </Typography>
          }
        />
      )}
      {isDetailItemEditable && isRearranged && isLiveFeature && (
        <Notification
          status="info"
          message={
            <Typography variant="body2">
              <Trans
                i18nKey="rearrangeItemsWarning"
                components={{ b: <b /> }}
                values={{ effectiveType: rearrangeEffectiveType }}
              />
            </Typography>
          }
        />
      )}
      <S.TopContent>
        <DetailItem bodyStyle={{ padding: 0, alignItems: 'center' }} title={`${t('items')}:`}>
          {collectionSize}
        </DetailItem>
        <S.ButtonGroup>
          {products?.length > 1 && (
            <>
              <Button
                startIcon={
                  <Tooltip
                    content={t('rearrangeItemsInfo')}
                    size="md"
                    tooltipConfig={{ placement: 'top-end' }}
                  >
                    <Icon name="tooltip" />
                  </Tooltip>
                }
                variant="link"
                color="primary"
                emphasis="800"
                disabled={!isDetailItemEditable}
                onClick={() => setRearrangeModalOpen(true)}
              >
                {t('rearrangeItems')}
              </Button>
              <RearrangeModal
                products={products}
                updateProducts={(...arg) => {
                  setIsRearranged(true);
                  updateProducts(...arg);
                }}
                open={rearrangeModalOpen}
                onClose={handleRearrangeModalClose}
              />
              <VerticalDivider $height="24px" />
            </>
          )}
          <Button
            startIcon={<Icon name="edit" />}
            disabled={!isDetailItemEditable}
            variant="link"
            color="primary"
            onClick={setShowProductModal}
            emphasis="800"
          >
            {t('edit')}
          </Button>
        </S.ButtonGroup>
      </S.TopContent>
      <GiftCollectionSizeModal
        isOpen={showSizeWarnOpen}
        setIsOpen={setShowSizeWarnOpen}
        primaryButtonProps={{
          children: t('editGiftCollection'),
          onClick: sizeModalHandler
        }}
      />
      {!isFetching && noItemsFound && !!originalProductLength && (
        <Notification
          status="warning"
          isClosable
          message={<Trans i18nKey="unavailableProductsWarning" count={originalProductLength} />}
        />
      )}
    </S.VerticalListHeader>
  );
}

VerticalListHeader.propTypes = {
  products: PropTypes.array.isRequired,
  updateProducts: PropTypes.func.isRequired,
  isDetailItemEditable: PropTypes.bool,
  hasAlert: PropTypes.bool,
  setShowProductModal: PropTypes.func,
  isFetching: PropTypes.bool,
  noItemsFound: PropTypes.bool,
  originalProductLength: PropTypes.number
};

export { VerticalListHeader };
